import { extractTrackingDate } from './trackingDateUtils';
import { getAvatarUrl } from './getAvatarUrl';
import { IssueFragment } from './gqlModels';
import { getNoteId, getProjectId } from './idUtils';
import { Project, IssueShortened, Issue, TimeLog, IncorrectTimeNote } from './model';

const issueMapShortened =
  ({ project, projects }: { project: Project; projects: Project[] }) =>
  (data: IssueFragment): IssueShortened => {
    const obj: IssueShortened = {
      id: data.id,
      iid: data.iid,
      title: data.title,
      state: data.state,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
      webUrl: data.webUrl,
      labels: data.labels?.nodes?.map((l) => l.title) ?? [],
      time_stats: {
        time_estimate: data.timeEstimate,
        total_time_spent: data.totalTimeSpent,
      },
      milestone: data.milestone
        ? {
            createdAt: new Date(data.milestone.createdAt),
            title: data.milestone.title,
            dueDate: data.milestone.dueDate
              ? new Date(data.milestone.dueDate)
              : undefined,
          }
        : null,
      links:
        data.linkedWorkItems?.nodes?.map((l) => {
          const targetId = getProjectId(l.workItem.project.id);
          const target = projects.find((p) => p.id === targetId);
          if (!target) throw new Error('Project not found');
          return {
            id: l.workItem.id.replace('WorkItem', 'Issue'),
            project: target,
          };
        }) ?? [],
      timeLogsFullLength: data.timelogs?.count,
      notesFullLength: data.notes?.count,
      project,
    };
    return obj;
  };

export const issueMapFunction =
  ({ project, projects }: { project: Project; projects: Project[] }) =>
  (data: IssueFragment): Issue => {
    const obj: IssueShortened = issueMapShortened({ project, projects })(data);
    return {
      ...obj,
      timeLogs:
        data.timelogs?.nodes?.map<TimeLog>((l) => ({
          id: l.id,
          issue: obj,
          issueId: l.id,
          user: {
            url: l.user.webUrl,
            avatar: getAvatarUrl(l.user.avatarUrl),
            name: l.user.name,
            username: l.user.username,
          },
          timeSpent: l.timeSpent / 60 / 60,
          spentAt: new Date(l.spentAt),
          summary: l.summary ?? '',
        })) ?? [],
    };
  };

export type AddIncorrectTimeNoteFunction = (incorrectTimeNote: IncorrectTimeNote) => void;

export const checkIssueIncorrectTimeNotes = ({
  addIncorrectTimeNote,
  issues,
  project,
  projects,
  from,
  to,
}: {
  addIncorrectTimeNote: AddIncorrectTimeNoteFunction;
  issues: IssueFragment[];
  from: Date;
  to: Date;
  project: Project;
  projects: Project[];
}) => {
  const checkDateInRange = (date: Date) => {
    return date.getTime() >= from.getTime() && date.getTime() <= to.getTime();
  };
  const checkDateIsIncorrect = (createdAt: Date, trackingDate: Date) => {
    const noteInRange = checkDateInRange(trackingDate);
    const noteCreatedInRange = checkDateInRange(new Date(createdAt));
    return (
      noteInRange !== noteCreatedInRange ||
      (createdAt.getTime() <= from.getTime() && trackingDate.getTime() >= to.getTime()) ||
      (trackingDate.getTime() <= from.getTime() && createdAt.getTime() >= to.getTime())
    );
  };

  for (let issue of issues) {
    const notes = issue.notes.nodes ?? [];
    for (const note of notes) {
      const trackingDate = extractTrackingDate(note.body);
      if (!trackingDate) continue;
      if (checkDateIsIncorrect(new Date(note.createdAt), trackingDate)) {
        addIncorrectTimeNote({
          createdAt: new Date(note.createdAt),
          spentAt: trackingDate,
          noteId: getNoteId(note.id),
          shortIssue: issueMapShortened({ project, projects })(issue),
          author: {
            url: note.author.webUrl,
            avatar: getAvatarUrl(note.author.avatarUrl),
            name: note.author.name,
            username: note.author.username,
          },
        });
      }
    }
  }
};
